import { LOCAL_STORAGE_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import type { LocalStorageProduct } from '@bulknutrients/bulk-shared-lib/interfaces/LocalStorage';
import type {
  BaseProduct,
  Product,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import {
  deleteIndexedDBData,
  Stores,
} from '@bulknutrients/bulk-shared-lib/lib/local/db';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Icons } from '@components/Icons/Icons';
import { ProductCard } from '@components/ProductCard/ProductCard';
import { SlideInPanel } from '@components/SlideInPanel/SlideInPanelNoPortal';
import { BannerH2 } from '@components/Typography/Headings/headings';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { logBreadcrumb } from '@lib/logs';
import { useRouter } from 'next/router';
import { type ReactElement, useContext, useEffect, useState } from 'react';

export function RecentlyViewed(): ReactElement {
  const [error, setError] = useState(false);
  const [items, setItems] = useState<LocalStorageProduct[]>([]);
  const { getRecentlyViewedProducts } = useLocalStorage();
  const {
    recentlyViewedIsOpen,
    setRecentlyViewedIsOpen,
    recentlyViewedUpdates,
  } = useContext<UIContextInterface>(UIContext);

  const router = useRouter();

  // close the recentlt view on route change
  useEffect(() => {
    const handleRouteChange = (url, shallow) => {
      setRecentlyViewedIsOpen(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  // get local storage
  useEffect(() => {
    let mounted = true;
    const setLocalItems = async () => {
      try {
        const localItems = await getRecentlyViewedProducts();
        if (localItems) {
          // Should be fine, but if one of the products is archived or missing available dates, it will fail type check
          const transformedItems = localItems.map((item) => {
            // biome-ignore lint/correctness/noUnusedVariables: this is how we are "deleting" description from the object
            const { description, ...productWithoutDescription } =
              item.product as Product;
            return {
              ...item,
              product: productWithoutDescription as BaseProduct,
            };
          });
          try {
            if (mounted) setItems(transformedItems);
          } catch (err) {
            console.error(err);
            await deleteIndexedDBData(
              Stores.keyvaluepairs,
              LOCAL_STORAGE_KEYS.RECENTLY_VIEWED_PRODUCTS
            );
            if (mounted) setItems([]);
          }
        } else if (mounted) setItems([]);
      } catch (error) {
        logBreadcrumb({
          category: 'IndexedDB',
          message: 'Tried to get recently viewed products',
          level: 'error',
        });
        setError(true);
      }
    };

    setLocalItems();

    return function cleanup() {
      mounted = false;
    };
  }, [items.length, recentlyViewedUpdates]);

  if (error) {
    return (
      <SlideInPanel
        isOpen={recentlyViewedIsOpen}
        toggleState={setRecentlyViewedIsOpen}
        testId="recently-viewed"
      >
        <Paragraph>Error loading recent items</Paragraph>
      </SlideInPanel>
    );
  }
  if (!items || items.length === 0) {
    return (
      <SlideInPanel
        isOpen={recentlyViewedIsOpen}
        toggleState={setRecentlyViewedIsOpen}
        displayCloseX={false}
        testId="recently-viewed"
      >
        <div className="flex justify-between items-center gap-2 mx-4 my-1">
          <h2 className="mb-1 mt-2.5">No Recent Items</h2>
          <button
            type="button"
            onClick={() => setRecentlyViewedIsOpen(false)}
            className="opacity-50 border-none bg-black dark:bg-white rounded-full font-secondary text-4xl text-white dark:text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:text-orange focus:no-underline"
          >
            <Icons.Close />
          </button>
        </div>
      </SlideInPanel>
    );
  }

  return (
    <SlideInPanel
      isOpen={recentlyViewedIsOpen}
      toggleState={setRecentlyViewedIsOpen}
      displayCloseX={false}
      testId="recently-viewed"
    >
      <section className="flex flex-col">
        <div className="flex justify-between items-center gap-2 mx-4 my-1">
          <h2 className="mb-1 mt-2.5">Recently Viewed Products</h2>
          <button
            type="button"
            onClick={() => setRecentlyViewedIsOpen(false)}
            className="opacity-50 border-none bg-black dark:bg-white rounded-full font-secondary text-4xl text-white dark:text-black hover:cursor-pointer hover:text-orange hover:no-underline focus:cursor-pointer focus:text-orange focus:no-underline"
          >
            <Icons.Close />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-5 self-center">
          <BulkErrorBoundary location="Recently viewed product cards">
            {items.map((item, i) => (
              <ProductCard
                product={item.product}
                key={`recentItem-${i}`}
                location="RecentlyViewed"
              />
            ))}
          </BulkErrorBoundary>
        </div>
      </section>
    </SlideInPanel>
  );
}
